import { Injectable } from '@angular/core';
import {
    GetActiveLicensesResponse,
    LicensingApiService,
    LicenseWithDefinition,
    MainLicenseId,
} from '@kempoweroyj/ngx-licensing-api-client';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthService } from 'providers/auth.service';
import { EnvironmentService } from 'providers/environment.service';

import { BehaviorSubject, Observable, catchError, combineLatest, filter, from, map, of, switchMap } from 'rxjs';

const LICENSE_TYPES_BY_IMPORTANCE: MainLicenseId[] = [
    'chargeye.v2.pro',
    'chargeye.v2.advanced',
    'chargeye.v2.basic',
    'mykempower.v2.core',
    'chargeye.depot-master',
    'chargeye.depot-advanced',
    'chargeye.depot-basic',
    'chargeye.cpo-advanced',
    'chargeye.cpo-basic',
    'chargeye.trial',
    'chargeye.warranty',
    'chargeye.depotmaster',
    'chargeye.advanced',
    'chargeye.basic-trimmed',
    'chargeye.service',
    'chargeye.oem',
    'chargeye.loadbalancing',
    'chargeye.evtracking',
    'chargeye.basic', // early access
];

const licenseInfo$ = new BehaviorSubject<LicenseWithDefinition>(null);

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
/**
 * This Service is used to store license information
 */
export class LicenseInfoService {
    private _licenseInfo$ = licenseInfo$.asObservable();

    get licenseInfo(): LicenseWithDefinition {
        return licenseInfo$.value;
    }
    get licenseInfo$(): Observable<LicenseWithDefinition> {
        return this._licenseInfo$;
    }

    constructor(
        private auth: AuthService,
        private licensingApi: LicensingApiService,
        private readonly environmentService: EnvironmentService
    ) {
        this.auth.authenticated$.subscribe({
            complete: () => {
                // Clear license info when user logs out
                licenseInfo$.next(null);
            },
        });

        // fetch license info when user logs in
        this.auth.authenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                switchMap(() => this.fetchLicenseData$())
            )
            .subscribe({
                next: (license) => {
                    if (license) {
                        licenseInfo$.next(license);
                    }
                },
            });
    }

    /**
     * Manually trigger a license refresh (when impersonating to another organization)
     * @returns Promise that resolves when the license has been refreshed
     */
    refreshLicense(): Promise<void> {
        return new Promise((resolve) => {
            this.fetchLicenseData$().subscribe({
                next: (license) => {
                    if (license) {
                        licenseInfo$.next(license);
                    }
                    resolve();
                },
            });
        });
    }

    /**
     * Fetch and process license data
     * @private
     */
    private fetchLicenseData$(): Observable<LicenseWithDefinition | null> {
        return combineLatest<[string, GetActiveLicensesResponse]>([
            this.environmentService.getAppOrigin$(),
            this.requestMyLicenses$(),
        ]).pipe(
            map(([origin, licenseResponse]) => {
                if (!licenseResponse) {
                    return null;
                }
                const mykempower = origin === 'mykempower';
                const activeLicenses: LicenseWithDefinition[] = licenseResponse.licenses || [];
                const licenseTypes = activeLicenses
                    .filter(
                        (l) =>
                            LICENSE_TYPES_BY_IMPORTANCE.includes(l.type) &&
                            (!mykempower || l.type.startsWith('mykempower'))
                    )
                    .map((l) => l.type);
                const activeLicenseType = LICENSE_TYPES_BY_IMPORTANCE.find((lt) => licenseTypes.includes(lt)) || '';
                const activeLicense = activeLicenses.find((l) => l.type === activeLicenseType);
                return activeLicense;
            })
        );
    }

    requestMyLicenses$() {
        return from(this.licensingApi.getActiveLicenses()).pipe(
            catchError((err) => {
                console.error(`licensingApi#getActiveLicenses ${JSON.stringify(err, null, 2)}`);
                return of(null);
            })
        );
    }

    /**
     * Determine if user should be redirected between platforms
     * @returns Observable containing redirect requirement info
     * - targetOrigin: 'mykempower' or 'chargeye'
     *   ('chargeye' represents all ChargEye platforms: chargeye.io, chargeye.au, kempower.io)
     */
    getRedirectRequirement$(): Observable<{
        shouldRedirect: boolean;
        targetOrigin: 'mykempower' | 'chargeye';
    }> {
        return combineLatest([this.isMyKempower$, this.environmentService.getAppOrigin$()]).pipe(
            map(([isMyKempower, currentOrigin]) => {
                // Never redirect when user is impersonating
                if (this.auth.profile?.impersonated) {
                    return {
                        shouldRedirect: false,
                        targetOrigin: currentOrigin,
                    };
                }

                // ONLY redirect case: MyKempower license + on ChargEye = redirect to MyKempower
                if (isMyKempower && currentOrigin === 'chargeye') {
                    return {
                        shouldRedirect: true,
                        targetOrigin: 'mykempower' as const,
                    };
                }

                // All other cases: no redirect - stay on current origin
                return {
                    shouldRedirect: false,
                    targetOrigin: currentOrigin,
                };
            })
        );
    }

    /**
     * Check if the current instance is running under MyKempower license
     * @returns True if this is a MyKempower instance
     */
    isMyKempower$: Observable<boolean> = licenseInfo$.asObservable().pipe(
        map((licenseResponse) => {
            if (licenseResponse?.type === 'mykempower.v2.core') {
                return true;
            }
            return false;
        })
    );
}
